import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  TrashIcon,
  EyeIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Checkbox from '@/components/ui/Checkbox';
import LoadingOverlay from '@/components/ui/LoadingOverlay';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  TableEmpty,
} from '@/components/ui/Table';
import { apiClient } from '@/lib/api';
import type { TTSJob, CombinedStats } from '@/types/api';
import { getJobTitle, getJobVoice, formatDuration } from '@/types/api';

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge variant="success" dot>Completed</Badge>;
    case 'running':
    case 'processing':
      return <Badge variant="primary" dot>Running</Badge>;
    case 'pending':
      return <Badge variant="warning" dot>Pending</Badge>;
    case 'failed':
      return <Badge variant="danger" dot>Failed</Badge>;
    default:
      return <Badge variant="slate">{status}</Badge>;
  }
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getJobDuration(job: TTSJob): string {
  if (!job.finished_at || !job.created_at) return '--';
  const start = new Date(job.created_at).getTime();
  const end = new Date(job.finished_at).getTime();
  return formatDuration(end - start);
}

export default function TTSJobsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);

  // Real data state
  const [jobs, setJobs] = useState<TTSJob[]>([]);
  const [stats, setStats] = useState<CombinedStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Extract unique voices from jobs for filter
  const voiceOptions = useMemo(() => {
    const voices = new Set<string>();
    jobs.forEach((job) => {
      const voice = getJobVoice(job);
      if (voice && voice !== 'Unknown') voices.add(voice);
    });
    return [
      { value: '', label: 'All Voices' },
      ...Array.from(voices).map((v) => ({ value: v, label: v })),
    ];
  }, [jobs]);

  const [voiceFilter, setVoiceFilter] = useState('');

  // Fetch jobs from backend
  const fetchJobs = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);
      else setIsRefreshing(true);

      const response = await apiClient.get<TTSJob[]>('tts/jobs');
      setJobs(response);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Fetch stats from backend
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.get<CombinedStats>('tts/queue/stats');
      setStats(response);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchJobs();
    fetchStats();
  }, [fetchJobs, fetchStats]);

  // Poll for updates every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchJobs(false);
      fetchStats();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchJobs, fetchStats]);

  // Filter jobs based on search, status, and voice
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const title = getJobTitle(job);
      const voice = getJobVoice(job);
      const matchesSearch =
        title.toLowerCase().includes(search.toLowerCase()) ||
        job.job_id.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = !statusFilter || job.status === statusFilter;
      const matchesVoice = !voiceFilter || voice === voiceFilter;
      return matchesSearch && matchesStatus && matchesVoice;
    });
  }, [jobs, search, statusFilter, voiceFilter]);

  const toggleJob = (jobId: string) => {
    setSelectedJobs((prev) =>
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const toggleAll = () => {
    if (selectedJobs.length === filteredJobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(filteredJobs.map((j) => j.job_id));
    }
  };

  // Stats from real data or fallback
  const totalJobs = stats?.job_metrics.total_jobs ?? jobs.length;
  const pendingCount = stats?.queue_stats.pending_jobs ?? jobs.filter((j) => j.status === 'pending').length;
  const runningCount = stats?.queue_stats.processing_jobs ?? jobs.filter((j) => j.status === 'running' || j.status === 'processing').length;
  const failedCount = stats?.queue_stats.failed_jobs ?? jobs.filter((j) => j.status === 'failed').length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Conversion Jobs</h1>
          <p className="text-slate-500 mt-1">Manage and monitor your TTS conversion jobs</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />}
            onClick={() => fetchJobs(false)}
            disabled={isRefreshing}
          >
            Refresh
          </Button>
          <Link to="/tts/upload">
            <Button leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}>
              New Upload
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search jobs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-40"
            />
            <Select
              options={voiceOptions}
              value={voiceFilter}
              onChange={(e) => setVoiceFilter(e.target.value)}
              className="w-48"
            />
            <Button variant="outline" leftIcon={<FunnelIcon className="w-4 h-4" />}>
              More Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats Summary - Connected to Real Data */}
      <LoadingOverlay isLoading={isLoading} loadingOpacity={40} showSpinner={false}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total Jobs</p>
            <p className="text-2xl font-bold text-slate-800">{totalJobs}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Pending</p>
            <p className="text-2xl font-bold text-warning-500">{pendingCount}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Running</p>
            <p className="text-2xl font-bold text-primary-500">{runningCount}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Failed</p>
            <p className="text-2xl font-bold text-danger-500">{failedCount}</p>
          </div>
        </div>
      </LoadingOverlay>

      {/* Jobs Table */}
      <LoadingOverlay isLoading={isLoading} loadingOpacity={40}>
        <Card padding="none">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow isHoverable={false}>
                  <TableHeaderCell className="w-12">
                    <Checkbox
                      checked={selectedJobs.length === filteredJobs.length && filteredJobs.length > 0}
                      onChange={toggleAll}
                    />
                  </TableHeaderCell>
                  <TableHeaderCell>Job</TableHeaderCell>
                  <TableHeaderCell>Voice</TableHeaderCell>
                  <TableHeaderCell>Progress</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Duration</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                  <TableHeaderCell className="w-32">Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {error ? (
                  <TableEmpty
                    message="Failed to load jobs"
                    description={error}
                    colSpan={8}
                  />
                ) : filteredJobs.length === 0 && !isLoading ? (
                  <TableEmpty
                    message="No jobs found"
                    description="Try adjusting your filters or upload a new file"
                    colSpan={8}
                  />
                ) : (
                  filteredJobs.map((job) => (
                    <TableRow key={job.id} isSelected={selectedJobs.includes(job.job_id)}>
                      <TableCell>
                        <Checkbox
                          checked={selectedJobs.includes(job.job_id)}
                          onChange={() => toggleJob(job.job_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/tts/jobs/${job.job_id}`}
                          className="hover:text-primary-500 transition-colors"
                        >
                          <p className="font-medium text-slate-800">{getJobTitle(job)}</p>
                          <p className="text-xs text-slate-400 font-mono">{job.job_id}</p>
                        </Link>
                      </TableCell>
                      <TableCell className="text-slate-600">{getJobVoice(job)}</TableCell>
                      <TableCell className="text-slate-600">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden max-w-[80px]">
                            <div
                              className={`h-full rounded-full transition-all ${
                                job.status === 'failed'
                                  ? 'bg-red-500'
                                  : job.status === 'completed'
                                  ? 'bg-emerald-500'
                                  : 'bg-primary-500'
                              }`}
                              style={{ width: `${job.progress}%` }}
                            />
                          </div>
                          <span className="text-xs">{job.progress}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(job.status)}</TableCell>
                      <TableCell className="text-slate-600">
                        {getJobDuration(job)}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {formatDate(job.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Link to={`/tts/jobs/${job.job_id}`}>
                            <Button variant="ghost" size="sm" className="p-2">
                              <EyeIcon className="w-4 h-4" />
                            </Button>
                          </Link>
                          {job.status === 'completed' && (
                            <Link to={`/tts/player/${job.job_id}`}>
                              <Button variant="ghost" size="sm" className="p-2">
                                <PlayIcon className="w-4 h-4" />
                              </Button>
                            </Link>
                          )}
                          {job.status === 'failed' && (
                            <Button variant="ghost" size="sm" className="p-2">
                              <ArrowPathIcon className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Bulk Actions */}
          {selectedJobs.length > 0 && (
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                {selectedJobs.length} job{selectedJobs.length > 1 ? 's' : ''} selected
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" leftIcon={<ArrowPathIcon className="w-4 h-4" />}>
                  Retry Failed
                </Button>
                <Button variant="danger" size="sm" leftIcon={<TrashIcon className="w-4 h-4" />}>
                  Delete Selected
                </Button>
              </div>
            </div>
          )}
        </Card>
      </LoadingOverlay>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Showing {filteredJobs.length} of {jobs.length} jobs
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled>
            Previous
          </Button>
          <Button variant="outline" size="sm" className="bg-primary-50 border-primary-200 text-primary-600">
            1
          </Button>
          <Button variant="outline" size="sm" disabled>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
